<template>
  <div>
    <vue-loading v-if="loading"></vue-loading>
    <div v-else>
      <div class="row">
        <div class="col-12">
          <h2>Items</h2>
        </div>
      </div>
      <div class="row hidden-sm-down" id="qm-itemlist-header">
        <div class="col-md-4 col-sm-12">Name</div>
        <div class="col-md-2 col-sm-6">{{ volumeUnit }}</div>
        <div class="col-md-3 col-sm-6">Count</div>
        <div class="col-md-2 col-sm-6">Mode</div>
        <div class="col-md-1 col-sm-6"></div>
      </div>
      <div class="row qm-panel-mb-1">
        <div class="col-12">
          <ul v-if="countRoomItems > 0" class="list-group" id="qm-item-list">
            <item
              v-for="item in roomItems"
              v-if="item.roomId === session.roomId"
              :item="item"
              :key="item.itemId"
              :volumeUnit="volumeUnit"
              :clientLanguage="clientLanguage"
              :standardSurveyItemsOrdered="standardSurveyItemsOrdered"
              :modeOptions="modeOptions"
            ></item>
          </ul>
          <p v-else class="qm-empty-message">
            <i class="fa fa-exclamation-triangle"></i>
            No items available yet
          </p>
          <p>
            <button v-on:click="addNewItem" type="button" class="btn btn-turquois">
              <i class="fa fa-fw fa-plus"></i>
              Add Item
            </button>
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-12 col-sm-12">
          <div class="qm-panel">
            <volumes v-bind:surveyId="this.session.surveyId"></volumes>
          </div>
        </div>
      </div>
      <div class="row" style="height: 320px">
        <!--
          Spacer. Workaround for Safari on iPad. On "Add Item", the keyboard would overlap the dropdown list
          and sometimes scroll the page too much.
          Empirical values:
          - 320px: All 14 drop-down list entries visible
          - 250px: 12 list entries visible
          - 150px:  8 list entries visible
          -   0px:  3 list entries visible
         -->
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-env browser */
import { mapGetters } from 'vuex'
// import $ from 'jquery'
import Item from './Item'
import Volumes from './Volumes'
export default {
  name: 'ItemList',
  components: {
    'volumes': Volumes,
    'item': Item
  },
  data: () => ({
    roomItems: [],
    countRoomItems: 0,
    standardSurveyItemsOrdered: [],
    clientLanguage: null,
    defaultModeway: null,
    loading: true
  }),
  computed: {
    ...mapGetters({
      session: 'getSession',
      survey: 'getSurvey',
      items: 'getAllItems',
      modeOptions: 'getAllModes',
      standardSurveyItems: 'getAllStandardSurveyItems'
    }),
    volumeUnit () {
      if (this.$hasMetricUnits()) {
        return 'Cbm'
      } else {
        return 'Cuft'
      }
    },
    weightUnit () {
      if (this.$hasMetricUnits()) {
        return 'Kg'
      } else {
        return 'lbs'
      }
    }
  },
  created: function () {
    this.$store.dispatch('loadSession').then(() => {
      this.$store.dispatch('getSurveyById', this.session.surveyId).then(() => {
        this.setClientLanguage()
        this.$store.dispatch('loadAllModes')
        this.$store.dispatch('loadItems', 'survey-' + this.session.surveyId + '-').then(() => {
          this.$store.dispatch('loadAllStandardSurveyItems').then(() => {
            this.updateRoomItems()
            this.setStandardSurveyItemsOrdered()
            this.setDefaultModeway()
            this.loading = false
          })
        })
      })
    })
  },
  methods: {
    setDefaultModeway: function () {
      this.defaultModeway = null
      if (!!this.survey.defaultMode && this.survey.defaultMode !== null) {
        this.defaultModeway = this.survey.defaultMode
      }
    },
    setClientLanguage: function () {
      if (!!this.survey.clientLanguage && this.survey.clientLanguage !== null) {
        this.clientLanguage = this.survey.clientLanguage
      }
    },
    setStandardSurveyItemsOrdered: function () {
      const language = this.clientLanguage
      const languageStandardSurveyItems = this.standardSurveyItems.map((standardSurveyItem) => {
        if (language && standardSurveyItem.name[language]) {
          return {
            value: standardSurveyItem.id,
            label: standardSurveyItem.name[language],
            volume: standardSurveyItem.volume,
            weight: standardSurveyItem.weight
          }
        }
        return {
          value: standardSurveyItem.id,
          label: standardSurveyItem.name[Object.keys(standardSurveyItem.name)[0]],
          volume: standardSurveyItem.volume,
          weight: standardSurveyItem.weight
        }
      })
      this.standardSurveyItemsOrdered = languageStandardSurveyItems.sort((a, b) => {
        let labelA = a.label.toUpperCase()
        let labelB = b.label.toUpperCase()
        if (labelA < labelB) { return -1 }
        if (labelA > labelB) { return 1 }
        return 0
      })
    },
    updateRoomItems () {
      this.roomItems = this.items.filter((item) => {
        return item.roomId === this.session.roomId
      })
      this.countRoomItems = this.roomItems.length
    },
    addNewItem () {
      /*
       * Workaround for iOS Safari trying to be a smartass and failing miserably to provide a decent user experience:
       * - Safari only shows the keyboard when a text field gets focused as a direct result of a touch event.
       *     Here, focus only gets set in a Promise, some ns later. The fix is to give focus to a dummyInput element that
       *     gets deleted as soon as focus got transferred to the final element.
       * - Safari has weird scrolling behavior when the keyboard overlaps the end of the document.
       * - When at the end of the page, Safari confusingly animates page scrolling, thereby destroying visual context.
       * See also: https://blog.opendigerati.com/the-eccentric-ways-of-ios-safari-with-the-keyboard-b5aa3f34228d
       */
      let dummyInput = document.createElement('input')
      document.body.appendChild(dummyInput)
      dummyInput.focus()

      const newItemStoreKey = 'survey-' + this.session.surveyId + '-room-' + this.session.roomId + '-item-' + this.session.nextItemId
      let emptyItem = {
        itemId: this.session.nextItemId,
        roomId: this.session.roomId,
        surveyId: this.session.surveyId,
        itemName: '',
        itemNameId: null,
        weight: null,
        volume: null,
        count: 1,
        mode: this.defaultModeway,
        disassembly: false,
        disassembly3rd: false,
        assembly: false,
        assembly3rd: false,
        packing: '',
        packedByOwner: false,
        crateType: null,
        crateL: null,
        crateW: null,
        crateH: null,
        comments: null
      }
      this.$store.dispatch('addItem', {
        storageKey: newItemStoreKey,
        item: emptyItem
      }).then(() => {
        this.updateRoomItems()
        this.$store.dispatch('incrementNextItemId').then(() => {
          // Workarounds for Safari on iPad:
          let search = this.$children[this.$children.length - 1].$refs.itemSelect.$refs.search
          search.focus()
          dummyInput.remove()
        })
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/children/ItemList.vue